'use client';
import React, { createContext, useCallback, useContext, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Snackbar from '@mui/material/Snackbar';
import { Close } from '../assets/icons/Close';
/**
 * Initialize with empty value.
 */
const SnackbarContext = createContext({
    showSnackbar: () => { },
    closeSnackbar: () => { },
});
/**
 * Returns a function that can be called to show the global snackbar, with updated values
 * specified by the caller.
 */
export const useSnackbar = () => {
    const { showSnackbar, closeSnackbar } = useContext(SnackbarContext);
    return { showSnackbar, closeSnackbar };
};
/**
 * Used at the top level of the app to provide the global snackbar.
 */
export const SnackbarProvider = ({ children }) => {
    const closeSnackbar = useCallback(() => setState((prevState) => {
        return Object.assign(Object.assign({}, prevState), { snackbarOpen: false });
    }), []);
    const defaultAction = React.createElement(Close, { onClick: closeSnackbar });
    // State has to be an object, or you get race conditions between various updates happening. Things run out of order.
    const [state, setState] = useState({
        snackbarOpen: false,
        title: '',
        severity: 'alertSuccess',
        action: defaultAction,
    });
    // This is the function that actually updates state when used by the various snackbar users.
    const showSnackbar = useCallback(({ title: callbackTitle, text: callbackText, severity: callbackSeverity, action: callbackAction = defaultAction, }) => setState({
        snackbarOpen: true,
        title: callbackTitle,
        text: callbackText,
        severity: callbackSeverity,
        action: callbackAction,
    }), []);
    const isErrorSnackbar = state.severity === 'alertError';
    return (React.createElement(SnackbarContext.Provider, { value: { showSnackbar, closeSnackbar } },
        children,
        React.createElement(Snackbar, { open: state.snackbarOpen, onClose: closeSnackbar, 
            // if it's an error alert, don't auto-hide, otherwise timeout is 3s
            autoHideDuration: isErrorSnackbar ? null : 3000, 
            // if it's an error alert, disable clicking/touching away to close, must click close action
            ClickAwayListenerProps: isErrorSnackbar ? { mouseEvent: false, touchEvent: false } : {} },
            React.createElement(Alert
            // take up 90% of screen space if there's a subtext, otherwise will take up the space of the
            // content alone (i.e. icon, title, action)
            , { 
                // take up 90% of screen space if there's a subtext, otherwise will take up the space of the
                // content alone (i.e. icon, title, action)
                sx: Object.assign({}, (state.text ? { width: '90%' } : {})), onClose: closeSnackbar, severity: state.severity, action: state.action },
                React.createElement(AlertTitle, null, state.title),
                state.text))));
};
